import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Тема витрины. Хранятся три значения, а переключатель light↔dark живёт в
 * разделе «Личные данные» — «по системе» возвращается отдельной кнопкой
 * (rules/i18n.md §3.2).
 *
 * Важно: тёмная тема в дизайн-системе ЦМО **объявлена, но не нарисована**
 * (`:root[data-theme="dark"]` — заглушка). Стор готов и `resolved` считает
 * честно, но АТРИБУТ на странице до отрисовки темы не должен говорить `dark`.
 */
export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

type ThemeState = {
	preference: ThemePreference;
	/** Производное значение: не попадает в persist и пересчитывается при гидрации. */
	resolved: ResolvedTheme;
	setPreference: (preference: ThemePreference) => void;
	/** Вызывается подпиской на `prefers-color-scheme`, когда выбран `system`. */
	syncSystem: () => void;
};

function systemTheme(): ResolvedTheme {
	if (typeof window === "undefined" || !window.matchMedia) return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(preference: ThemePreference): ResolvedTheme {
	return preference === "system" ? systemTheme() : preference;
}

/**
 * Нарисована ли тёмная тема в дизайн-системе.
 *
 * Пока `false`, на страницу уходит только `light` — даже человеку с тёмной ОС.
 * Причина не в осторожности: `[data-theme="dark"]` в токенах — заглушка, и
 * атрибут на `<html>` означал бы «эта страница тёмная» ровно в тот момент,
 * когда рисовать её нечем. Первое же настоящее правило под этим селектором
 * сломало бы витрину у всех, у кого в системе тёмная тема, — без единой правки
 * в этом файле.
 *
 * Включение темы = поменять эту константу и нарисовать токены. Одно место.
 */
const DARK_THEME_DRAWN = false;

/** Атрибут читают токены дизайн-системы; класса-дублёра в IEC ID нет. */
function applyTheme(resolved: ResolvedTheme): void {
	if (typeof document === "undefined") return;
	document.documentElement.dataset.theme =
		resolved === "dark" && !DARK_THEME_DRAWN ? "light" : resolved;
}

export const useThemeStore = create<ThemeState>()(
	persist(
		(set, get) => ({
			preference: "system",
			resolved: resolve("system"),
			setPreference: (preference) => {
				const resolved = resolve(preference);
				applyTheme(resolved);
				set({ preference, resolved });
			},
			syncSystem: () => {
				if (get().preference !== "system") return;
				const resolved = systemTheme();
				applyTheme(resolved);
				set({ resolved });
			},
		}),
		{
			name: "iec-id.theme",
			// Только источник истины. `resolved` — производное, оно пересчитывается
			// в onRehydrateStorage; иначе после reload остаётся стартовое значение.
			partialize: (state) => ({ preference: state.preference }),
			onRehydrateStorage: () => (state) => {
				if (!state) return;
				const resolved = resolve(state.preference);
				applyTheme(resolved);
				state.resolved = resolved;
			},
		},
	),
);
